// --- Our Modules --------------------------------------------------------------------------------
import type { Translation } from "@/translations/types";
import type { TyInteraction, TyLead, TyLeadStage } from "@/types";
// --- Logic --------------------------------------------------------------------------------------
type TyTone = "available" | "reserved" | "sold" | "rented" | "neutral" | "accent" | "danger";

/**
 * What colour a stage reads as. Only the two ends are coloured as outcomes — green for won, red for
 * lost — and everything between them is the same working amber on purpose. A pipeline that darkens
 * as a deal advances tells a person the later stages matter more, which is the opposite of true: a
 * deal stuck at `offer` is worse news than one that arrived this morning.
 */
export function stageTone(stage: TyLeadStage): TyTone {
    if (stage === "won") return "available";
    if (stage === "lost") return "danger";
    if (stage === "new") return "accent";
    return "reserved";
}

/** A lead has no name of its own. It is a person and the thing they want, so that is what it is called. */
export function leadTitle(lead: TyLead, t: Translation, readName: (value: unknown) => string): string {
    const unit = typeof lead.unit === "object" && lead.unit ? lead.unit.number : null;
    const project = typeof lead.project === "object" && lead.project ? readName(lead.project.name) : null;

    if (unit && project) return `${project} · ${unit}`;
    if (project) return project;
    if (unit) return unit;
    return t.crm.general;
}

export type TyDueState = "overdue" | "upcoming" | "done";

/**
 * Where an entry sits against today. `status` is the outcome and `null` means there is not one yet,
 * so a row still empty after its date has passed is **overdue**, and the same row before its date is
 * simply booked. No stored field holds this — it is the date and the outcome read together, which is
 * the whole reason a lead carries no "next action".
 */
export function dueState(interaction: Pick<TyInteraction, "status" | "date">): TyDueState {
    if (interaction.status) return "done";
    return new Date(interaction.date) <= new Date() ? "overdue" : "upcoming";
}

/** The soonest thing still owed on a lead, read off the timeline the screen already has. */
export function nextDue(interactions: TyInteraction[]): TyInteraction | null {
    const open = interactions.filter((entry) => !entry.status).sort((a, b) => +new Date(a.date) - +new Date(b.date));
    return open[0] ?? null;
}
